import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { DHIKR_HADITHS, DhikrHadith } from '../../data/dhikrHadiths';

const CRAWLER_UA =
  /googlebot|bingbot|slurp|duckduckbot|baiduspider|yandexbot|facebookexternalhit|twitterbot|linkedinbot|embedly|quora link preview|showyoubot|outbrain|pinterest|applebot|semrushbot|ahrefsbot|mj12bot|dotbot|petalbot|bytespider|reactsnap/i;

function shouldSkip(): boolean {
  if (typeof navigator === 'undefined') return true;
  if (navigator.userAgent === 'ReactSnap') return true;
  return CRAWLER_UA.test(navigator.userAgent);
}

function pickRandomHadith(): DhikrHadith {
  const index = Math.floor(Math.random() * DHIKR_HADITHS.length);
  return DHIKR_HADITHS[index];
}

const DhikrWelcomePopup: React.FC = () => {
  const [hadith, setHadith] = useState<DhikrHadith | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (shouldSkip()) return;
    const next = pickRandomHadith();

    const timer = window.setTimeout(() => {
      setHadith(next);
      window.requestAnimationFrame(() => setVisible(true));
    }, 400);

    return () => window.clearTimeout(timer);
  }, []);

  const dismiss = () => {
    setVisible(false);
    window.setTimeout(() => setHadith(null), 200);
  };

  useEffect(() => {
    if (!hadith) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') dismiss();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [hadith]);

  if (!hadith) return null;

  return (
    <div
      className={`fixed inset-0 z-[70] flex items-end justify-center p-3 sm:items-center sm:p-4 transition-opacity duration-200 ${
        visible ? 'bg-black/45 opacity-100 backdrop-blur-[2px]' : 'bg-black/0 opacity-0'
      }`}
      dir="rtl"
      onClick={dismiss}
      role="presentation"
    >
      <div
        className={`relative flex max-h-[min(88vh,40rem)] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-green-100 bg-white shadow-2xl shadow-green-900/15 transition-all duration-300 ${
          visible ? 'translate-y-0 scale-100 opacity-100' : 'translate-y-4 scale-95 opacity-0 sm:translate-y-2'
        }`}
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="dhikr-welcome-title"
      >
        <div className="h-1.5 w-full shrink-0 bg-gradient-to-l from-gold-400 via-gold-500 to-green-700" />

        <button
          type="button"
          onClick={dismiss}
          className="absolute left-2 top-3.5 flex min-h-11 min-w-11 items-center justify-center rounded-xl text-gray-400 hover:bg-gray-50 hover:text-gray-600"
          aria-label="إغلاق"
        >
          <X size={18} />
        </button>

        <div className="overflow-y-auto px-4 pt-5 sm:px-6 sm:pt-6">
          <p className="mb-2 text-xs font-semibold text-gold-600 sm:text-sm">حديث شريف</p>
          <h2
            id="dhikr-welcome-title"
            className="font-cairo-bold pe-8 text-xl leading-snug text-green-800 sm:text-2xl"
          >
            {hadith.title}
          </h2>

          <p className="mt-4 text-sm font-semibold text-green-700 sm:text-base">قال رسول الله ﷺ:</p>

          <blockquote className="mt-2 rounded-xl border border-green-100 border-s-4 border-s-gold-500 bg-[#f4f1ea] px-3 py-3 text-sm leading-8 text-green-950 sm:px-4 sm:py-4 sm:text-base sm:leading-9">
            «{hadith.text}»
          </blockquote>

        </div>

        <div className="shrink-0 px-4 pb-4 pt-3 sm:px-6 sm:pb-6">
          <p className="mb-3 text-xs leading-relaxed text-gold-700 sm:text-sm">📚 {hadith.source}</p>
          <button
            type="button"
            onClick={dismiss}
            className="flex min-h-12 w-full items-center justify-center rounded-xl bg-green-700 px-4 text-sm font-bold text-white shadow-md shadow-green-900/20 transition-colors hover:bg-green-800 sm:text-base"
          >
            تم
          </button>
        </div>
      </div>
    </div>
  );
};

export default DhikrWelcomePopup;
